import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, Link as LinkIcon, Loader2 } from 'lucide-react';

const COUNTRIES = [
  { code: 'MCO', name: 'Colombia', flag: '🇨🇴' },
  { code: 'MLA', name: 'Argentina', flag: '🇦🇷' },
  { code: 'MLM', name: 'México', flag: '🇲🇽' },
  { code: 'MLC', name: 'Chile', flag: '🇨🇱' },
  { code: 'MPE', name: 'Perú', flag: '🇵🇪' },
  { code: 'MLB', name: 'Brasil', flag: '🇧🇷' },
];

const ConnectMeli = () => {
  const navigate = useNavigate();
  const { user, loginWithMeli, logout } = useAuth();
  const [pendingSite, setPendingSite] = useState(null);

  useEffect(() => {
    // Check if there's a pending site from landing page
    const site = localStorage.getItem('pendingMeliSite');
    if (site) {
      setPendingSite(site);
    }
  }, []);

  const handleConnect = (siteCode) => {
    // Clear the pending site
    localStorage.removeItem('pendingMeliSite');
    loginWithMeli(siteCode);
  };

  // Redirects must happen in an effect, not during render — calling navigate()
  // while this component is rendering can leave the page stuck showing the
  // `null` render below without the navigation ever actually committing.
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    } else if (user.meli_connected) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  if (!user || user.meli_connected) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold font-outfit text-foreground mb-2">
            ¡Cuenta creada!
          </h1>
          <p className="text-muted-foreground">
            Ahora conecta tu cuenta de Mercado Libre para empezar
          </p>
        </div>

        {/* User Info */}
        {user && (
          <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6 text-center">
            <p className="text-muted-foreground text-sm">Conectado como</p>
            <p className="text-foreground font-medium">{user.email}</p>
          </div>
        )}

        {/* Country Selection */}
        <div className="bg-card/60 backdrop-blur-xl border border-border rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Selecciona tu país
          </h2>

          {pendingSite && (
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-3">
              País preseleccionado: {COUNTRIES.find(c => c.code === pendingSite)?.name}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            {COUNTRIES.map((country) => (
              <Button
                key={country.code}
                variant="outline"
                onClick={() => handleConnect(country.code)}
                className={`h-14 border-border hover:border-blue-500 hover:bg-blue-500/10 text-foreground justify-start gap-3 ${
                  pendingSite === country.code ? 'border-blue-500 bg-blue-500/10' : ''
                }`}
              >
                <span className="text-2xl">{country.flag}</span>
                <span>{country.name}</span>
              </Button>
            ))}
          </div>

          <p className="text-muted-foreground/70 text-xs text-center mt-4">
            Serás redirigido a Mercado Libre para autorizar el acceso
          </p>
        </div>

        {/* Skip / Logout */}
        <div className="flex justify-center gap-4 mt-6">
          <Button
            variant="ghost"
            onClick={logout}
            className="text-muted-foreground/70 hover:text-muted-foreground"
          >
            Cerrar sesión
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConnectMeli;
